
import { WPXFormValue } from '@/components/templates/WPXForm';
import CreateFtpUserForm from '@/services/FtpUser/forms/CreateFtpUserForm';
import { FtpUserRepositoryInterface } from '@/services/FtpUser/interfaces';
import {
  ServerInterface,
  ServerRepositoryInterface,
} from '@/services/Server/Interfaces';
import WebsiteRepositoryInterface, {
  Website,
} from '@/services/Website/interfaces';
import { AxiosError } from 'axios';
import { Component, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { SystemUserRepositoryInterface } from '@/services/SystemUsers/interfaces';

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'ftpUserRepository',
    'serverRepository',
    'websiteRepository',
    'systemUserRepository',
  ],
})
export default class CreateFtpUser extends Vue {
  public loading: boolean = true;

  public form: WPXFormValue<CreateFtpUserForm> =
    new WPXFormValue<CreateFtpUserForm>(CreateFtpUserForm);

  public ftpUserRepository!: FtpUserRepositoryInterface;
  public serverRepository!: ServerRepositoryInterface;
  public websiteRepository!: WebsiteRepositoryInterface;
  public systemUserRepository!: SystemUserRepositoryInterface;

  public servers: ServerInterface[] = [];
  public websites: Website[] = [];

  public _websiteSearch: string = '';
  public websiteSearchLoading: boolean = false;

  public async created(): Promise<void> {
    await this.initialize();
  }

  public async initialize(): Promise<void> {
    try {
      this.loading = true;
      await this.getServers();
    } catch (err) {
      this.handleError(err);
    } finally {
      this.loading = false;
    }
  }

  public async getServers(): Promise<void> {
    this.servers = await this.serverRepository.getAllServers();
  }

  public generateWebsiteSearchString(val: string, server: string): string {
    const searchParams = new URLSearchParams();
    searchParams.set('server', server);
    searchParams.set('domain', val);

    return searchParams.toString();
  }

  public get websiteSearch(): string {
    return this._websiteSearch;
  }

  public set websiteSearch(val: string) {
    if (val && val.length < 3) return;

    const {
      data: { server },
    } = this.form;
    this._websiteSearch = this.generateWebsiteSearchString(val, server);
    this.websiteSearchLoading = true;

    this.websiteRepository
      .getByServerAndDomain(server, val)
      .then((websites) => {
        this.websites = websites;
      })
      .catch((err) => {
        this.handleError(err);
      })
      .finally(() => {
        this.websiteSearchLoading = false;
      });
  }

  public async createFtpUser(): Promise<void> {
    const website = this.websites.find(
      (w) => w.id === this.form.data.websiteId,
    );
    if (!website) {
      this.$errorDialog.show('Website not found!');
      return;
    }

    try {
      await this.ftpUserRepository.createFtpUser({
        server: this.form.data.server,
        websiteId: this.form.data.websiteId,
        systemUserId: Number(website?.details.unixUserId),
        username: this.form.data.username,
        password: this.form.data.password,
        homeDir: this.form.data.homeDir,
      });

      this.$successDialog.show(`FTP User ${this.form.data.username} creation started!`);
    } catch (err) {
      this.handleError(err);
    }
  }

  @Watch('form.data.websiteId')
  public async fetchSystemUser(): Promise<void> {
    if (!this.form.data.websiteId) {
      return;
    }

    const website = this.websites.find((w) => w.id === this.form.data.websiteId);
    if (!website) {
      return;
    }

    const domain = website.domain.startsWith('xn--') ?
        website.domain.replace('xn--', '') :
        website.domain;

    try {
      const systemUser = await this.systemUserRepository.findSystemUserById(website.details.unixUserId);
      this.form.data.homeDir = systemUser.websiteId === website.id
        ? `${systemUser.directory}/public_html`
        : `${systemUser.directory}/domains/${domain}/public_html`;

    } catch (err) {
      this.$errorDialog.show(`Failed to get system user with id: ${website.id}`);
    }
  }

  private handleError(err: unknown) {
    const axiosErr = err as AxiosError;
    if (axiosErr.isAxiosError) this.$errorDialog.show(axiosErr.response?.data?.message);
    else if (err instanceof Error) this.$errorDialog.show(err.message);
  }
}
